import { memo, useEffect, useRef } from 'react';

import { useFabricCanvas, useFabricCanvasWindow } from './hooks';
import useFabricCanvasEvents from './hooks/useFabricCanvasEvents';

import { DESIGN_COUNTERTOP_ACTIONS_HEIGHT } from '~/core/constants';
import { useEstimateContext } from '~/legacy/easy-estimator-page/context/estimateContext';
import { useAppDispatch } from '~/redux/hooks';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { enableAllSteps } from '~/redux/slices/stepper';
import { visitAllSteps } from '~/redux/slices/visitedSteps';
import { usePricedProductsContext } from '~contexts';

type FabricCanvasProps = {
  width: number;
};

const FabricCanvas = memo(({ width }: FabricCanvasProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { windowHeight } = useFabricCanvasWindow(width);
  const { canvasRef } = useFabricCanvas();

  usePricedProductsContext();
  useFabricCanvasEvents();

  const { loadEstimateCode } = useEstimateContext();
  const enabledAllSteps = useRef<boolean>(false);

  useEffect(() => {
    // TODO: This is a temporary solution because of an issue with the stepper listeners.
    // We are enabling all steps after the estimate is fully restored, but when the
    // selected material is updated, for example, it calls the onUpdate on the stepperListeners
    // which ends up doing 20+ calls to updateSelectedStore, and then the updateSelectedMaterial
    // which resets the enabled step to 'store' and 'material' only, resulting in, after everything,
    // the enabledSteps are ['store', 'material', 'color']. Curiously, the navStep stays "enabled".
    // This is a workaround to ensure that all steps are enabled after the estimate is loaded.
    // and the user first loads the design page.
    // The updateNeedRedrawCanvas is what displays the shape when first loading the design page.
    if (loadEstimateCode && !enabledAllSteps.current) {
      dispatch(enableAllSteps());
      dispatch(visitAllSteps());
      dispatch(updateNeedRedrawCanvas(true));
      enabledAllSteps.current = true;
    }
  }, [dispatch, loadEstimateCode]);

  // set the height of the canvas to be 70% of the view port extracting the height of the bottom items (rotate, delete)
  return (
    <canvas
      ref={canvasRef}
      id="fabric-canvas"
      width={width}
      height={0.7 * windowHeight - DESIGN_COUNTERTOP_ACTIONS_HEIGHT}
    />
  );
});

FabricCanvas.displayName = 'FabricCanvas';
export default FabricCanvas;
